/**
 * Rate-change gate (rate-change protection): mounted on labeling
 * surfaces (Data Manager labeling stream, Bulk label). Polls the
 * pending unacknowledged rate changes for the current user on this
 * project; when one exists, a BLOCKING modal shows the old → new
 * rates side by side with exactly two choices:
 *
 *   Accept & continue        — recorded; work resumes.
 *   Reject — pay me my work  — recorded; participation ends, the
 *                              validated balance stays owed, and the
 *                              owner is notified.
 *
 * No dismiss without deciding — that's the point: an owner cannot
 * change the deal silently.
 */
import { useCallback, useEffect, useState } from "react";
import { Button, useToast } from "@humansignal/ui";
import { Modal } from "../Modal/Modal";
import { spaNavigate } from "../../utils/navigation";

const getCsrfToken = () => {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

const KIND_LABELS = {
  annotation_accepted: "Accepted annotation",
  vote_cast: "Validation vote",
  prompt_accepted: "Accepted prompt",
  clip_validated: "Validated clip",
};

const fmtRate = (v, currency) => (v == null ? "—" : `${v} ${currency || ""}`.trim());

export const RateChangeGate = ({ projectId }) => {
  const toast = useToast();
  const [change, setChange] = useState(null);
  const [spanCount, setSpanCount] = useState(1);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await fetch(`/api/projects/${projectId}/rate-changes/pending/`, {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!r.ok) return;
      const b = await r.json();
      const changes = b.changes || [];
      if (changes.length === 0) {
        setChange(null);
        setSpanCount(1);
        return;
      }
      // Multiple rapid edits collapse into ONE decision: show the
      // span — the OLDEST pending change's old rates (the last state
      // the contributor worked under) against the NEWEST change's new
      // rates (the card as it is now). Acking the newest change
      // acknowledges the intermediates server-side.
      const newest = changes[0];
      const oldest = changes[changes.length - 1];
      setSpanCount(changes.length);
      setChange({
        ...newest,
        old_rates: oldest.old_rates,
        old_currency: oldest.old_currency,
      });
    } catch {
      /* network blip — try again next poll */
    }
  }, [projectId]);

  useEffect(() => {
    check();
    // Mid-session changes surface while actively annotating.
    const id = window.setInterval(check, 60_000);
    return () => window.clearInterval(id);
  }, [check]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const r = await fetch(`/api/projects/${projectId}/rate-changes/${change.id}/ack/`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
        body: JSON.stringify({ decision }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.show({ type: "error", message: b.detail || "Could not record your decision. Try again." });
        return;
      }
      if (decision === "exited") {
        const amt = b.owed_amount ? `${b.owed_amount} ${b.owed_currency || ""}`.trim() : null;
        toast.show({
          message: amt
            ? `You've left the project. Your validated balance of ${amt} stays owed to you, the organization has been notified.`
            : "You've left the project. The organization has been notified.",
          duration: 9000,
        });
        spaNavigate("/projects", true);
        return;
      }
      toast.show({ message: "New rates accepted, carry on." });
      setChange(null);
      check();
    } finally {
      setBusy(false);
    }
  };

  if (!change) return null;

  const kinds = [...new Set([...Object.keys(change.old_rates || {}), ...Object.keys(change.new_rates || {})])];

  return (
    <Modal
      visible
      onHide={() => {
        /* deciding is the only way out */
      }}
      title="The pay rates on this project changed"
      style={{ width: 560 }}
      allowClose={false}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: "var(--font-size-body-medium)", lineHeight: 1.55 }}>
          The project owner changed the rate card
          {spanCount > 1 ? ` (${spanCount} changes since you last reviewed)` : ""}
          {change.scope === "all_unpaid"
            ? ", including your validated work that hasn't been paid yet."
            : ". Work you've already done keeps the rate it was done under."}{" "}
          Review the change and choose how to continue.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-body-small)" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--color-neutral-content-subtler, #64748b)" }}>
              <th style={{ padding: "4px 6px" }}>Unit</th>
              <th style={{ padding: "4px 6px" }}>Was</th>
              <th style={{ padding: "4px 6px" }}>Now</th>
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => {
              const oldV = change.old_rates?.[k];
              const newV = change.new_rates?.[k];
              const worse = Number(newV ?? 0) < Number(oldV ?? 0);
              return (
                <tr key={k} style={{ borderTop: "1px solid var(--color-neutral-border-subtle, #e2e8f0)" }}>
                  <td style={{ padding: "6px" }}>{KIND_LABELS[k] ?? k}</td>
                  <td style={{ padding: "6px" }}>{fmtRate(oldV, change.old_currency)}</td>
                  <td
                    style={{
                      padding: "6px",
                      fontWeight: 700,
                      color: worse
                        ? "var(--color-negative-content, #c0353a)"
                        : "var(--color-positive-content, #1f7a5a)",
                    }}
                  >
                    {fmtRate(newV, change.new_currency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            type="button"
            variant="negative"
            look="outlined"
            size="medium"
            disabled={busy}
            onClick={() => decide("exited")}
          >
            Reject — pay out my work
          </Button>
          <Button type="button" variant="primary" size="medium" disabled={busy} onClick={() => decide("accepted")}>
            Accept & continue
          </Button>
        </div>
      </div>
    </Modal>
  );
};
